import { useContext, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import withSidebar from '../../../../../hoc/withSideBar/withSideBar';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import MlxDropDown from '../../../../../components/common/MlxDropDown/MlxDropDown';
import { AuthCtx } from '../../../../../context/AuthCtxProvider';
import { OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../../constants/constants';
import { _VenuePickerOption } from '../../../../../constants/staticTypes';
import { MENU_IMPORT_BATCH, MENU_IMPORT_BATCHES, MENU_IMPORT_QUOTA } from '../../../../../constants/queryKeys';
import {
    BULK_UPLOAD,
    MENU_IMPORT_ADD_REFUSED_MSG,
    MENU_IMPORT_ADD_UNAVAILABLE_MSG,
    MENU_IMPORT_CLOSE,
    MENU_IMPORT_COMMIT_FAILED_MSG,
    MENU_IMPORT_COMMIT_FAILED_TITLE,
    MENU_IMPORT_CONTINUE_REVIEWING,
    MENU_IMPORT_ERROR_CONCURRENT,
    MENU_IMPORT_ERROR_GENERIC,
    MENU_IMPORT_ERROR_IN_PROGRESS,
    MENU_IMPORT_ERROR_LIMIT,
    MENU_IMPORT_ERROR_MONTHLY,
    MENU_IMPORT_ERROR_TITLE,
    MENU_IMPORT_ERROR_UNAVAILABLE,
    MENU_IMPORT_ERROR_UPLOAD,
    MENU_IMPORT_LOAD_ERROR,
    MENU_IMPORT_NO_PERMISSION,
    MENU_IMPORT_NO_VENUE,
    MENU_IMPORT_NOTHING_TO_ADD_MSG,
    MENU_IMPORT_NOTHING_TO_ADD_TITLE,
    MENU_IMPORT_PREVIOUS_MSG,
    MENU_IMPORT_PREVIOUS_TITLE,
    MENU_IMPORT_RETRY,
    MENU_IMPORT_REVIEW_CONFLICT_MSG,
    MENU_IMPORT_REVIEW_CONFLICT_TITLE,
    MENU_IMPORT_SUCCESS_COMPLETE,
    MENU_IMPORT_SUCCESS_COUNT,
    MENU_IMPORT_SUCCESS_PARTIAL,
    MENU_IMPORT_SUCCESS_PARTIAL_NOTE,
    MENU_IMPORT_SUCCESS_TITLE,
    MENU_IMPORT_SUBTITLE,
    MENU_IMPORT_VIEW_MENU,
    SELECT_VENUE,
} from '../../../../../constants/strings';
import { JTranslation } from '../../../../../helpers/jTranslationHelper';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { getVenueOptions } from '../../../../../helpers/venueHelper';
import {
    _MenuImportBatchResponse,
    _MenuImportBatchSummary,
    _MenuImportCreateBody,
    _MenuImportError,
    _MenuImportErrorKind,
    _MenuImportFileMeta,
    cancelBatch,
    classifyMenuImportError,
    createBatch,
    createUploadForms,
    findOpenBatch,
    formatUtcDate,
    finishBatch,
    getBatch,
    getBatches,
    getImportQuota,
    getImportMimeType,
    flattenDraft,
    getImportStep,
    getOrgImportQuota,
    getPublishOutcome,
    regenerateBatch,
    resumeBatch,
    uploadFileToS3,
} from '../../../../../helpers/menuImportHelper';
import { routes } from '../../../../../routes/allRoutes';
import { checkActionSubPermission } from '../../../../../utils/common';
import ExtractionStep from './components/ExtractionStep';
import FailedStep from './components/FailedStep';
import ImportMessageDialog, { _ImportMessage } from './components/ImportMessageDialog';
import QuotaSummary from './components/QuotaSummary';
import ReviewStep from './components/ReviewStep';
import StepsStrip, { toStripStep } from './components/StepsStrip';
import UploadStep from './components/UploadStep';
import './BulkUpload.scss';

const languageTexts = {
    BULK_UPLOAD: BULK_UPLOAD,
    SELECT_VENUE: SELECT_VENUE,
};

const EXTRACTION_POLL_MS = 3000;

// The 429 kinds: the create was refused because of an allowance, so the quota summary may be stale.
const QUOTA_REFUSALS: _MenuImportErrorKind[] = ['concurrentLimit', 'monthlyLimit', 'limitReached'];

// A publish this page started and is waiting on; the outcome popup shows only for it, never for a batch found already finished.
type _PublishWatch = { batchId: string; selectedCount: number; committedBefore: number; sawAdding: boolean };
type _DialogActions = { onPrimary: () => void; onSecondary?: () => void };

const countCommitted = (batch?: { draftTree?: Parameters<typeof flattenDraft>[0] }) =>
    flattenDraft(batch?.draftTree).filter((row) => row.committed).length;

const errorMessage = (message: string, detail?: string): _ImportMessage => ({
    variant: 'error',
    title: MENU_IMPORT_ERROR_TITLE,
    message,
    detail,
});

const toImportMessage = (error: _MenuImportError): _ImportMessage => {
    switch (error.kind) {
        case 'importInProgress':
            return errorMessage(MENU_IMPORT_ERROR_IN_PROGRESS);
        case 'concurrentLimit':
            return errorMessage(MENU_IMPORT_ERROR_CONCURRENT);
        case 'monthlyLimit':
            return errorMessage(MENU_IMPORT_ERROR_MONTHLY, error.data?.resetsAt ? formatUtcDate(error.data.resetsAt) : undefined);
        case 'limitReached':
            return errorMessage(MENU_IMPORT_ERROR_LIMIT);
        case 'unavailable':
        case 'network':
            return errorMessage(MENU_IMPORT_ERROR_UNAVAILABLE);
        default:
            return errorMessage(MENU_IMPORT_ERROR_GENERIC);
    }
};

function BulkUpload() {
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const { userType } = useContext(AuthCtx);

    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenue, setSelectedVenue] = useState<string>();
    const [isVenueListLoading, setIsVenueListLoading] = useState(true);
    const [phase, setPhase] = useState<'idle' | 'uploading'>('idle');
    const [files, setFiles] = useState<File[]>([]);
    const [menuName, setMenuName] = useState('');
    const [dialog, setDialog] = useState<_ImportMessage | null>(null);
    const [pendingBody, setPendingBody] = useState<_MenuImportCreateBody | null>(null);
    const [activeBatchId, setActiveBatchId] = useState<string>();
    const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(new Set());
    const [isActing, setIsActing] = useState(false);
    const afterDialog = useRef<(() => void) | null>(null);
    const publishWatch = useRef<_PublishWatch | null>(null);
    const dialogActions = useRef<_DialogActions | null>(null);
    const navigate = useNavigate();

    const canUpload =
        userType === 'organizationAdmin' ||
        checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE_MENU, OrgSubPermissionKeys.ADD_VENUE_MENU);

    useEffect(() => {
        const loadVenues = async () => {
            const options = await getVenueOptions(queryClient);
            setVenueOptions(options);
            if (options?.length > 0) setSelectedVenue(options[0].id);
            setIsVenueListLoading(false);
        };
        loadVenues();
    }, []);

    const batchesQuery = useQuery(
        [MENU_IMPORT_BATCHES, selectedVenue],
        () => getBatches(queryClient, selectedVenue as string, { limit: 10 }),
        { enabled: !!selectedVenue && canUpload, refetchOnWindowFocus: false }
    );

    const quotaQuery = useQuery([MENU_IMPORT_QUOTA, selectedVenue], () => getImportQuota(queryClient, selectedVenue as string), {
        enabled: !!selectedVenue && canUpload,
    });
    const orgQuota = getOrgImportQuota(quotaQuery.data?.data?.data);
    const quotaSummary = (
        <QuotaSummary
            quota={orgQuota}
            isLoading={quotaQuery.isLoading}
            isError={quotaQuery.isError || (quotaQuery.isSuccess && !orgQuota)}
        />
    );

    // Dismissal is applied after the lookup so a dismissed newest row cannot let an older FAILED row take its place.
    const foundBatch: _MenuImportBatchSummary | undefined = findOpenBatch(batchesQuery.data?.data?.data ?? []);
    const openBatch = foundBatch && !dismissedIds.has(foundBatch.id) ? foundBatch : undefined;
    const batchId = activeBatchId ?? openBatch?.id;

    const batchQuery = useQuery(
        [MENU_IMPORT_BATCH, selectedVenue, batchId],
        () => getBatch(queryClient, selectedVenue as string, batchId as string),
        {
            enabled: !!selectedVenue && !!batchId && canUpload,
            refetchOnWindowFocus: false,
            refetchInterval: (response) => {
                const stepNow = getImportStep(response?.data?.data?.status);
                return stepNow === 'extracting' || stepNow === 'adding' ? EXTRACTION_POLL_MS : false;
            },
        }
    );

    const batch = batchQuery.data?.data?.data;
    const status = batch?.status ?? (openBatch?.id === batchId ? openBatch?.status : undefined);
    const step = phase === 'uploading' ? 'extracting' : batchId ? getImportStep(status) : 'upload';
    const batchLoadFailed = batchQuery.isError && !batch;
    const isLocked = phase === 'uploading' || ((step === 'extracting' || step === 'adding') && !batchLoadFailed);

    const invalidateBatches = () => queryClient.invalidateQueries([MENU_IMPORT_BATCHES, selectedVenue]);
    const invalidateQuota = () => queryClient.invalidateQueries([MENU_IMPORT_QUOTA, selectedVenue]);

    const adoptBatch = (response: _MenuImportBatchResponse) => {
        const created = response.data.data;
        queryClient.setQueryData([MENU_IMPORT_BATCH, selectedVenue, created.id], response);
        setActiveBatchId(created.id);
        invalidateBatches();
    };

    const showError = (error: unknown, after?: () => void) => {
        afterDialog.current = after ?? null;
        setDialog(toImportMessage(classifyMenuImportError(error)));
    };

    const closeDialog = () => {
        setDialog(null);
        setPendingBody(null);
        dialogActions.current = null;
        const after = afterDialog.current;
        afterDialog.current = null;
        after?.();
    };

    const createWithBody = async (body: _MenuImportCreateBody) => {
        try {
            adoptBatch(await createBatch(queryClient, selectedVenue as string, body));
            invalidateQuota();
            setFiles([]);
            setMenuName('');
        } catch (error) {
            const classified = classifyMenuImportError(error);
            if (classified.kind === 'previousImportExists') {
                setPendingBody(body);
                setDialog({
                    variant: 'warning',
                    title: MENU_IMPORT_PREVIOUS_TITLE,
                    message: MENU_IMPORT_PREVIOUS_MSG,
                });
            } else {
                // A refused create (429) means the allowance moved or was already spent, so the summary is read again.
                if (QUOTA_REFUSALS.includes(classified.kind)) invalidateQuota();
                showError(error, classified.kind === 'concurrentLimit' ? invalidateBatches : undefined);
            }
        } finally {
            setPhase('idle');
        }
    };

    const handleExtract = async (name: string) => {
        setMenuName(name);
        setPhase('uploading');

        const metas: _MenuImportFileMeta[] = files.map((file) => ({
            fileName: file.name,
            mimeType: getImportMimeType(file) as _MenuImportFileMeta['mimeType'],
            sizeBytes: file.size,
        }));

        let body: _MenuImportCreateBody;
        try {
            const forms = await createUploadForms(queryClient, selectedVenue as string, metas);
            const uploads = forms.data.data.uploads;
            try {
                if (uploads.length !== files.length) throw new Error('upload forms do not match the chosen files');
                await Promise.all(uploads.map((form, index) => uploadFileToS3(form, files[index])));
            } catch {
                setDialog(errorMessage(MENU_IMPORT_ERROR_UPLOAD));
                setPhase('idle');
                return;
            }
            body = {
                files: uploads.map((form, index) => ({ ...metas[index], fileKey: form.fileKey })),
                targetMenuName: name,
            };
        } catch (error) {
            setPhase('idle');
            showError(error);
            return;
        }

        await createWithBody(body);
    };

    const handleAcknowledge = () => {
        const body = pendingBody;
        setDialog(null);
        setPendingBody(null);
        if (!body) return;
        setPhase('uploading');
        createWithBody({ ...body, acknowledgePreviousImport: true });
    };

    const openDialog = (message: _ImportMessage, actions: _DialogActions) => {
        dialogActions.current = actions;
        setDialog(message);
    };

    const viewMenu = () => {
        closeDialog();
        navigate(routes.orgVenueFbMenu, { state: { venueId: selectedVenue } });
    };

    const startWatching = (id: string, selectedCount: number) => {
        publishWatch.current = { batchId: id, selectedCount, committedBefore: countCommitted(batch), sawAdding: false };
        // Keep this batch addressable: once it is COMPLETED it leaves the open-batch list.
        setActiveBatchId(id);
    };

    const handlePublished = (count: number) => {
        if (batchId) startWatching(batchId, count);
    };

    const showAddRefused = (message: _ImportMessage) => {
        openDialog(message, { onPrimary: closeDialog, onSecondary: closeDialog });
        queryClient.invalidateQueries([MENU_IMPORT_BATCH, selectedVenue]);
        invalidateBatches();
    };

    // A 400 from publish means nothing was left to add; a 409 is a state refusal (the batch moved on without this page).
    const handlePublishError = (error: unknown) => {
        const { kind } = classifyMenuImportError(error);
        if (kind === 'badRequest') showAddRefused(errorMessage(MENU_IMPORT_ADD_REFUSED_MSG));
        else if (kind === 'conflict') {
            showAddRefused({ variant: 'warning', title: MENU_IMPORT_REVIEW_CONFLICT_TITLE, message: MENU_IMPORT_REVIEW_CONFLICT_MSG });
        } else if (kind === 'unavailable') openDialog(errorMessage(MENU_IMPORT_ADD_UNAVAILABLE_MSG), { onPrimary: closeDialog });
        else showError(error);
    };

    const handleRetryCommit = async () => {
        if (!batchId || isActing) return;
        setIsActing(true);
        try {
            await resumeBatch(queryClient, selectedVenue as string, batchId);
            queryClient.setQueryData<_MenuImportBatchResponse | undefined>([MENU_IMPORT_BATCH, selectedVenue, batchId], (previous) =>
                previous ? { ...previous, data: { ...previous.data, data: { ...previous.data.data, status: 'COMMITTING' } } } : previous
            );
            startWatching(batchId, 0);
        } catch (error) {
            handlePublishError(error);
        } finally {
            setIsActing(false);
        }
    };

    const showOutcome = (watch: _PublishWatch, outcome: NonNullable<ReturnType<typeof getPublishOutcome>>) => {
        const venueName = venueOptions.find((venue) => venue.id === selectedVenue)?.venueName ?? '';
        const added = Math.max(countCommitted(batch) - watch.committedBefore, 0);
        const count = outcome === 'completed' ? watch.selectedCount || added : added || watch.selectedCount;
        const countFields = count > 0 ? { countLabel: MENU_IMPORT_SUCCESS_COUNT, count } : {};
        if (outcome === 'completed') {
            openDialog(
                {
                    variant: 'success',
                    title: MENU_IMPORT_SUCCESS_TITLE,
                    message: MENU_IMPORT_SUCCESS_COMPLETE,
                    detail: venueName,
                    ...countFields,
                    primaryLabel: MENU_IMPORT_VIEW_MENU,
                    secondaryLabel: MENU_IMPORT_CLOSE,
                },
                { onPrimary: viewMenu, onSecondary: closeDialog }
            );
        } else if (outcome === 'partial') {
            openDialog(
                {
                    variant: 'success',
                    title: MENU_IMPORT_SUCCESS_TITLE,
                    message: MENU_IMPORT_SUCCESS_PARTIAL,
                    detail: venueName,
                    ...countFields,
                    note: MENU_IMPORT_SUCCESS_PARTIAL_NOTE,
                    primaryLabel: MENU_IMPORT_VIEW_MENU,
                    secondaryLabel: MENU_IMPORT_CONTINUE_REVIEWING,
                },
                { onPrimary: viewMenu, onSecondary: closeDialog }
            );
        } else if (outcome === 'commitFailed') {
            openDialog(
                {
                    variant: 'error',
                    title: MENU_IMPORT_COMMIT_FAILED_TITLE,
                    message: MENU_IMPORT_COMMIT_FAILED_MSG,
                    primaryLabel: MENU_IMPORT_RETRY,
                    secondaryLabel: MENU_IMPORT_CLOSE,
                },
                {
                    onPrimary: () => {
                        closeDialog();
                        handleRetryCommit();
                    },
                    onSecondary: closeDialog,
                }
            );
        } else {
            openDialog(
                { variant: 'error', title: MENU_IMPORT_NOTHING_TO_ADD_TITLE, message: MENU_IMPORT_NOTHING_TO_ADD_MSG },
                { onPrimary: closeDialog }
            );
        }
    };

    // Watches a publish this page started: popups open when the batch leaves COMMITTING, never for an old batch.
    useEffect(() => {
        const watch = publishWatch.current;
        if (!watch || batch?.id !== watch.batchId) return;
        if (getImportStep(batch.status) === 'adding') {
            watch.sawAdding = true;
            return;
        }
        const outcome = getPublishOutcome(batch.status);
        if (!watch.sawAdding || !outcome) return;
        publishWatch.current = null;
        showOutcome(watch, outcome);
        invalidateBatches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch?.id, batch?.status]);

    const dismissBatch = (id: string) => setDismissedIds((previous) => new Set(previous).add(id));

    // Resolves `true` once the batch is gone, `false` when nothing happened or the call was refused (the review step relies on it).
    const handleCancelImport = async (): Promise<boolean> => {
        if (!batchId || isActing) return false;
        setIsActing(true);
        let ended = false;
        try {
            // A batch with items already added can no longer be cancelled (409); finishing is how it ends.
            const end = status === 'PARTIALLY_PUBLISHED' ? finishBatch : cancelBatch;
            await end(queryClient, selectedVenue as string, batchId);
            // Cancelling before extraction starts gives the import back; finishing does not, but a refresh is cheap.
            invalidateQuota();
            dismissBatch(batchId);
            setActiveBatchId(undefined);
            ended = true;
        } catch (error) {
            showError(error);
        } finally {
            setIsActing(false);
        }
        queryClient.invalidateQueries([MENU_IMPORT_BATCH, selectedVenue]);
        invalidateBatches();
        return ended;
    };

    const handleTryAgain = async () => {
        if (!batchId) return;
        setIsActing(true);
        try {
            adoptBatch(await regenerateBatch(queryClient, selectedVenue as string, batchId));
        } catch (error) {
            showError(error);
        } finally {
            setIsActing(false);
        }
    };

    // A FAILED batch cannot be cancelled, so there is no API call: the page only stops showing it.
    const handleUploadDifferent = () => {
        if (batchId) dismissBatch(batchId);
        setActiveBatchId(undefined);
    };

    const renderStep = () => {
        if (step === 'extracting') {
            return (
                <ExtractionStep
                    batch={batch ?? openBatch}
                    uploading={phase === 'uploading'}
                    fileCount={files.length || undefined}
                    fileNames={phase === 'uploading' ? files.map((file) => file.name) : undefined}
                    menuName={(batch ?? openBatch)?.targetMenuName || menuName || undefined}
                    onCancel={handleCancelImport}
                />
            );
        }
        if (step === 'failed') {
            return <FailedStep busy={isActing} onTryAgain={handleTryAgain} onUploadDifferent={handleUploadDifferent} />;
        }
        if (step === 'ready' || step === 'adding') {
            // The review table needs the draft, which only the batch detail carries; the block UI covers the wait.
            return batch ? (
                <ReviewStep
                    key={batch.id}
                    venueId={selectedVenue as string}
                    batch={batch}
                    busy={isActing}
                    onDiscard={handleCancelImport}
                    onFinish={handleCancelImport}
                    onPublished={handlePublished}
                    onPublishError={handlePublishError}
                    onRetry={handleRetryCommit}
                />
            ) : null;
        }
        return (
            <UploadStep
                key={selectedVenue}
                files={files}
                onFilesChange={setFiles}
                initialMenuName={menuName}
                onExtract={handleExtract}
                quotaExhausted={orgQuota !== null && orgQuota.remaining <= 0}
            />
        );
    };

    // The strip only describes a working import: it stays out of the no-permission, no-venue and load-error states.
    const showStrip =
        canUpload && !isVenueListLoading && !!selectedVenue && !batchesQuery.isLoading && !batchesQuery.isError && !batchLoadFailed;

    const renderLoadError = (onRetry: () => void) => (
        <div className="bulk-upload-card bulk-upload-state">
            <span className="bulk-upload-error-text">
                <JTranslation text={MENU_IMPORT_LOAD_ERROR} />
            </span>
            <MlxButton onClick={onRetry}>
                <JTranslation text={MENU_IMPORT_RETRY} />
            </MlxButton>
        </div>
    );

    const renderBody = () => {
        if (!canUpload) {
            return (
                <div className="bulk-upload-card bulk-upload-state">
                    <JTranslation text={MENU_IMPORT_NO_PERMISSION} />
                </div>
            );
        }
        if (isVenueListLoading) return null;
        if (!selectedVenue) {
            return (
                <div className="bulk-upload-card bulk-upload-state" data-testid="bulk-upload-no-venue">
                    <JTranslation text={MENU_IMPORT_NO_VENUE} />
                </div>
            );
        }
        if (batchesQuery.isError) return renderLoadError(() => batchesQuery.refetch());
        if (batchesQuery.isLoading) return null;
        if (batchLoadFailed) return renderLoadError(() => batchQuery.refetch());
        return renderStep();
    };

    return (
        <MlxBlockUI
            isLoading={isVenueListLoading || (canUpload && (batchesQuery.isLoading || (!!batchId && batchQuery.isLoading)))}
            opacity={0.5}
        >
            <div className="page-section h-100 bulk-upload">
                <span className="page-header d-flex justify-content-between bulk-upload-page-header">
                    <span className="bulk-upload-page-heading">
                        <span className="bulk-upload-page-title">{translatedText.BULK_UPLOAD ?? BULK_UPLOAD}</span>
                        <span className="bulk-upload-page-subtitle">
                            <JTranslation text={MENU_IMPORT_SUBTITLE} typeCase="none" />
                        </span>
                    </span>
                    <MlxDropDown
                        dropDownSize="large"
                        options={venueOptions}
                        optionLabel="venueName"
                        optionValue="id"
                        value={selectedVenue}
                        placeholder={translatedText.SELECT_VENUE ?? SELECT_VENUE}
                        onChange={(e) => {
                            setSelectedVenue(e.value);
                            setActiveBatchId(undefined);
                            setFiles([]);
                            setMenuName('');
                        }}
                        style={{ width: '200px' }}
                        disabled={isLocked}
                    />
                </span>
                {showStrip && <StepsStrip current={toStripStep(step)} quota={quotaSummary} />}
                <div className="page-content">
                    <div className="bulk-upload-body">{renderBody()}</div>
                </div>
            </div>
            <ImportMessageDialog
                message={dialog}
                onPrimary={dialogActions.current?.onPrimary ?? (dialog?.variant === 'warning' ? handleAcknowledge : closeDialog)}
                onSecondary={dialogActions.current?.onSecondary ?? closeDialog}
            />
        </MlxBlockUI>
    );
}

export default withSidebar(BulkUpload);
